<template>
  <AppShell :hero-mode="!isLoggedIn">
    <!-- Guest hero -->
    <PageHero
      v-if="!isLoggedIn"
      eyebrow="Cybergym"
      icon="fas fa-server"
      title="Machine Labs"
      subtitle="Boot real vulnerable machines over VPN and own them — just like a real engagement. Login to start labs and manage your VPN."
      cta="Login to Start"
      cta-href="/login"
      secondary="Create Account"
      secondary-href="/register"
    />

    <div :class="isLoggedIn ? '' : 'container-app py-10'">
    <!-- Compact heading (authed) -->
    <div v-if="isLoggedIn" class="mb-8">
      <p class="eyebrow"><i class="fas fa-server"></i> Cybergym</p>
      <h1 class="mt-2 text-3xl font-extrabold text-white">Machine Labs</h1>
      <p class="mt-2 text-ink-muted">Boot real vulnerable machines over VPN and practice hands-on exploitation.</p>
    </div>

    <!-- Authenticated dashboard -->
    <div v-if="isLoggedIn" class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
      <!-- Lab environment (2/3) -->
      <div class="lg:col-span-2 card p-6">
        <h2 class="text-lg font-bold text-white mb-5 flex items-center gap-2">
          <i class="fas fa-desktop text-brand"></i> Lab Environment
        </h2>

        <!-- Active instance -->
        <div v-if="usageStats.activeInstance" class="rounded-xl border border-line bg-surface-light p-5 mb-4">
          <h3 class="font-semibold text-white mb-4 flex items-center gap-2">
            <i class="fas fa-server text-info"></i> Active Machine Instance
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span class="text-ink-faint">Machine</span>
              <div class="font-bold text-white">{{ usageStats.activeInstance.MachineLab?.title || getLabTitle(usageStats.activeInstance.machineLabId) }}</div>
            </div>
            <div>
              <span class="text-ink-faint">IP Address</span>
              <div class="font-bold text-success font-mono">{{ usageStats.activeInstance.containerInfo?.ip || usageStats.activeInstance.ip || 'Configuring...' }}</div>
            </div>
            <div>
              <span class="text-ink-faint">Status</span>
              <div :class="getStatusClass(usageStats.activeInstance.containerInfo?.status || usageStats.activeInstance.status)" class="font-bold capitalize">
                {{ (usageStats.activeInstance.containerInfo?.status || usageStats.activeInstance.status || '') }}
              </div>
            </div>
          </div>
          <!-- Multi-service list -->
          <div v-if="usageStats.activeInstance.containerInfo?.services?.length > 1" class="mt-3 pt-3 border-t border-line">
            <div class="flex flex-wrap gap-2">
              <div v-for="svc in usageStats.activeInstance.containerInfo.services" :key="svc.service_name"
                class="flex items-center gap-2 bg-base-800 border border-line rounded px-2 py-1 text-xs">
                <span class="font-mono text-ink-muted">{{ svc.service_name }}</span>
                <code v-if="svc.hostname" class="text-brand font-mono">{{ svc.hostname }}</code>
                <code class="text-success font-mono font-bold">{{ svc.vpn_ip }}</code>
              </div>
            </div>
          </div>
          <div class="mt-4 pt-4 border-t border-line">
            <button @click="goToActiveLab" class="btn-primary">
              <i class="fas fa-external-link-alt"></i> Manage Machine
            </button>
          </div>
        </div>

        <!-- No active machine -->
        <div v-else class="rounded-xl border border-dashed border-line bg-surface-light/40 py-12 text-center">
          <i class="fas fa-server text-4xl text-ink-faint mb-3"></i>
          <p class="text-ink-muted">No machine instances running</p>
          <p class="text-sm text-ink-faint">Pick a lab below to spin one up</p>
        </div>

        <!-- Countdown -->
        <div v-if="usageStats.activeInstance && usageStats.activeInstance.scheduledShutdownAt" class="mt-4 flex items-center justify-between rounded-xl border border-yellow-600/40 bg-yellow-500/10 p-4">
          <div class="flex items-center gap-2 text-yellow-300 text-sm">
            <i class="fas fa-clock"></i> Auto-shutdown in
            <span class="font-bold text-yellow-100">{{ formatShutdownTime(usageStats.activeInstance.scheduledShutdownAt) }}</span>
          </div>
          <span class="text-xs text-yellow-400">
            Extensions: {{ usageStats.activeInstance.extensionCount || 0 }}/{{ usageStats.activeInstance.maxExtensions || 3 }}
          </span>
        </div>
      </div>

      <!-- Sidebar panels -->
      <div class="space-y-6">
        <!-- Usage stats -->
        <div class="card p-5">
          <h3 class="font-semibold text-white mb-4 flex items-center gap-2">
            <i class="fas fa-chart-bar text-info"></i> Usage Statistics
          </h3>
          <div class="space-y-3 text-sm">
            <div class="flex justify-between">
              <span class="text-ink-muted">Weekly</span>
              <span class="font-bold text-white">{{ usageStats.weeklyUsage }}/{{ usageStats.weeklyLimit }}</span>
            </div>
            <div class="h-1.5 rounded-full bg-surface-lighter overflow-hidden">
              <div class="h-full bg-brand-gradient rounded-full" :style="{ width: usagePct }"></div>
            </div>
            <div class="flex justify-between">
              <span class="text-ink-muted">Remaining</span>
              <span class="font-bold text-success">{{ usageStats.remainingUsage }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-ink-muted">Plan</span>
              <span :class="usageStats.isSubscribed ? 'text-success' : 'text-brand'" class="font-bold">
                {{ usageStats.isSubscribed ? 'Premium' : 'Free' }}
              </span>
            </div>
          </div>
        </div>

        <!-- VPN -->
        <div class="card p-5">
          <h3 class="font-semibold text-white mb-4 flex items-center gap-2">
            <i class="fas fa-shield-halved text-brand"></i> VPN Access
          </h3>
          <div class="space-y-2 mb-4">
            <button @click="downloadVPN" class="btn-primary w-full">
              <i class="fas fa-download"></i> Download VPN
            </button>
            <button @click="revokeVPN" class="btn-secondary w-full">
              <i class="fas fa-sync"></i> Regenerate
            </button>
          </div>
          <div class="rounded-lg border border-line bg-surface-light p-3 text-xs text-ink-muted space-y-1.5">
            <p><span class="text-brand font-bold">1.</span> Download VPN config</p>
            <p><span class="text-brand font-bold">2.</span> Install an OpenVPN client</p>
            <p><span class="text-brand font-bold">3.</span> Import the .ovpn file</p>
            <p><span class="text-brand font-bold">4.</span> Connect to access machines</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Toolbar: search + view toggle -->
    <div class="flex flex-col gap-4 mb-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="relative sm:max-w-xs w-full">
          <i class="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint text-sm"></i>
          <input v-model="searchQuery" type="text" placeholder="Search machine labs..." class="input pl-10" />
        </div>
        <div class="flex items-center gap-1 p-1 rounded-lg bg-surface-light border border-line self-start">
          <button @click="viewMode = 'grid'" :class="['p-1.5 rounded-md transition-colors', viewMode === 'grid' ? 'bg-surface-lighter text-white' : 'text-ink-faint hover:text-ink-muted']" title="Grid view">
            <i class="fas fa-grip text-sm"></i>
          </button>
          <button @click="viewMode = 'list'" :class="['p-1.5 rounded-md transition-colors', viewMode === 'list' ? 'bg-surface-lighter text-white' : 'text-ink-faint hover:text-ink-muted']" title="List view">
            <i class="fas fa-list text-sm"></i>
          </button>
        </div>
      </div>
      <!-- Status filter chips -->
      <div class="flex flex-wrap gap-2">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          @click="statusFilter = opt.value"
          :class="['chip cursor-pointer transition-colors', statusFilter === opt.value ? 'bg-brand/20 text-brand border-brand/40' : 'hover:border-ink-muted hover:text-ink']"
        >
          {{ opt.label }} <span class="ml-1 text-ink-faint">{{ statusCount(opt.value) }}</span>
        </button>
      </div>
    </div>

    <!-- Grid view -->
    <div v-if="visibleLabs.length && viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <a
        v-for="(lab, index) in visibleLabs"
        :key="index"
        :href="`/machine-labs/${lab.slug}`"
        class="card card-hover p-6 group flex flex-col"
      >
        <div class="flex items-center justify-between mb-4">
          <div class="h-11 w-11 grid place-items-center rounded-xl bg-brand/10 border border-brand/20 text-brand text-lg group-hover:scale-110 transition-transform">
            <i class="fas fa-server"></i>
          </div>
          <div class="flex gap-1.5">
            <span v-if="lab.isExclusive" class="badge bg-yellow-500 text-black"><i class="fas fa-crown"></i></span>
            <span v-if="lab.isCompleted" class="badge bg-success/15 text-success"><i class="fas fa-check"></i> Owned</span>
          </div>
        </div>
        <h2 class="text-lg font-bold text-white mb-2 line-clamp-1">{{ lab.title }}</h2>
        <p class="text-sm text-ink-muted line-clamp-3 flex-1">{{ lab.description }}</p>
        <span class="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
          Enter lab <i class="fas fa-arrow-right text-xs group-hover:translate-x-1 transition-transform"></i>
        </span>
      </a>
    </div>

    <!-- List view -->
    <div v-else-if="visibleLabs.length && viewMode === 'list'" class="card overflow-hidden divide-y divide-line">
      <a
        v-for="(lab, index) in visibleLabs"
        :key="index"
        :href="`/machine-labs/${lab.slug}`"
        class="flex items-center gap-4 p-4 hover:bg-surface-light transition-colors group"
      >
        <div class="h-10 w-10 grid place-items-center rounded-lg bg-brand/10 border border-brand/20 text-brand shrink-0">
          <i class="fas fa-server"></i>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <h3 class="font-semibold text-white truncate">{{ lab.title }}</h3>
            <span v-if="lab.isExclusive" class="badge bg-yellow-500 text-black"><i class="fas fa-crown"></i> Exclusive</span>
            <span v-if="lab.isCompleted" class="badge bg-success/15 text-success"><i class="fas fa-check"></i> Owned</span>
          </div>
          <p class="text-sm text-ink-muted line-clamp-1 mt-0.5">{{ lab.description }}</p>
        </div>
        <i class="fas fa-arrow-right text-ink-faint group-hover:text-brand group-hover:translate-x-1 transition-all shrink-0"></i>
      </a>
    </div>

    <!-- Empty -->
    <div v-else class="card p-12 text-center">
      <i class="fas fa-server text-4xl text-ink-faint mb-4"></i>
      <p class="text-ink-muted">No machine labs match your filters.</p>
    </div>
    </div>
  </AppShell>
</template>

<script>
import AppShell from "@/components/ui-components/AppShell.vue";
import PageHero from "@/components/ui-components/PageHero.vue";
import apiClient from "@/axiosConfig";
import toastr from "toastr";
import { isAuthenticated, redirectToLogin } from "@/utils/auth";

export default {
  name: "MachineLabsPage",
  components: { AppShell, PageHero },
  data() {
    return {
      searchQuery: "",
      viewMode: "grid",
      statusFilter: "all",
      statusOptions: [
        { value: "all", label: "All" },
        { value: "available", label: "Available" },
        { value: "completed", label: "Owned" },
        { value: "exclusive", label: "Exclusive" },
      ],
      labs: [],
      usageStats: {
        weeklyUsage: 0,
        weeklyLimit: 10,
        remainingUsage: 10,
        isSubscribed: false,
        activeInstance: null,
      },
      isLoggedIn: isAuthenticated(),
    };
  },
  computed: {
    filteredLabs() {
      const query = this.searchQuery.toLowerCase();
      return this.labs.filter(
        (lab) =>
          lab.title.toLowerCase().includes(query) || lab.description.toLowerCase().includes(query)
      );
    },
    visibleLabs() {
      return this.filteredLabs.filter((lab) => this.matchesStatus(lab, this.statusFilter));
    },
    usagePct() {
      const limit = this.usageStats.weeklyLimit || 1;
      return `${Math.min(100, (this.usageStats.weeklyUsage / limit) * 100)}%`;
    },
  },
  methods: {
    matchesStatus(lab, status) {
      if (status === "available") return !lab.isCompleted;
      if (status === "completed") return lab.isCompleted;
      if (status === "exclusive") return lab.isExclusive;
      return true; // "all"
    },
    statusCount(status) {
      return this.filteredLabs.filter((lab) => this.matchesStatus(lab, status)).length;
    },
    async fetchLabs() {
      try {
        const response = await apiClient.get("/machine-labs");
        this.labs = response.data.map((lab) => ({
          id: lab.id,
          title: lab.title,
          description: lab.description,
          isCompleted: lab.isCompleted,
          isExclusive: lab.isExclusive,
          slug: lab.id,
        }));
      } catch (error) {
        console.error("Error fetching Machine Labs:", error);
        this.labs = [
          { id: "c218426c-2cef-4b47-aae8-0d5c4eff0d91", title: "JS Obfuscation", description: "Reverse engineer and deobfuscate malicious JavaScript payloads.", isExclusive: true, isCompleted: false, slug: "c218426c-2cef-4b47-aae8-0d5c4eff0d91" },
          { id: 2, title: "Database Penetration", description: "Learn SQL injection and database security testing techniques.", isCompleted: true, isExclusive: false, slug: 2 },
          { id: 3, title: "Network Reconnaissance", description: "Discover and analyze network infrastructure using various scanning tools.", isCompleted: false, isExclusive: false, slug: 3 },
        ];
      }
    },
    async fetchUsageStats() {
      if (!isAuthenticated()) {
        this.usageStats = { weeklyUsage: 0, weeklyLimit: 15, remainingUsage: 15, isSubscribed: false, activeInstance: null };
        return;
      }
      try {
        const response = await apiClient.get("/machine-labs/usage");
        this.usageStats = response.data;
      } catch (error) {
        console.error("Error fetching usage stats:", error);
        this.usageStats = { weeklyUsage: 0, weeklyLimit: 15, remainingUsage: 15, isSubscribed: false, activeInstance: null };
      }
    },
    async downloadVPN() {
      if (!isAuthenticated()) return redirectToLogin();
      try {
        toastr.info("Preparing VPN configuration download...");
        const response = await apiClient.get("/machine-labs/vpn/download", { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "machine-lab-vpn.ovpn");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toastr.success("VPN configuration downloaded! Import it into your OpenVPN client.");
      } catch (error) {
        toastr.error(error.response?.data?.message || "Failed to download VPN configuration");
      }
    },
    async revokeVPN() {
      if (!isAuthenticated()) return redirectToLogin();
      try {
        await apiClient.post("/machine-labs/vpn/revoke");
        toastr.success("VPN profile revoked and regenerated. Download the new configuration.");
      } catch (error) {
        toastr.error(error.response?.data?.message || "Failed to revoke VPN profile");
      }
    },
    getLabTitle(labId) {
      const lab = this.labs.find((l) => l.id === labId);
      return lab ? lab.title : "Unknown Lab";
    },
    goToActiveLab() {
      if (this.usageStats.activeInstance) {
        this.$router.push(`/machine-labs/${this.usageStats.activeInstance.machineLabId}`);
      }
    },
    formatShutdownTime(dateString) {
      if (!dateString) return "";
      const diffMs = new Date(dateString) - new Date();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      if (diffMins <= 0) return "Expired";
      if (diffMins < 60) return `${diffMins}m`;
      const hours = Math.floor(diffMins / 60);
      return `${hours}h ${diffMins % 60}m`;
    },
    getStatusClass(status) {
      return { running: "text-success", starting: "text-yellow-400", stopped: "text-brand" }[status] || "text-ink-muted";
    },
  },
  async mounted() {
    this.isLoggedIn = isAuthenticated();
    await this.fetchLabs();
    await this.fetchUsageStats();
  },
};
</script>
